import React, { useMemo, useState } from 'react';
import {
  PreparedListItem,
  usePreparedPoolsRows
} from '@app/hooks/pools/usePreparedPoolsRows';
import { useLiquidityWizardState } from '@app/screens/liquidity/LiquidityWizard/state';
import { Box, Text, VStack } from '@chakra-ui/react';
import { VaultDetails } from '@app/screens/liquidity/LiquidityWizard/components/SelectTokensStep/components/VaultDetails';
import { PoolDetails } from '@app/screens/liquidity/LiquidityWizard/components/SelectTokensStep/components/PoolDetails';
import { PoolDetailsV2 } from '@app/screens/liquidity/LiquidityWizard/components/SelectTokensStep/components/PoolDetailsV2';

function arePairsEqual(a: string, b: string, c: string, d: string): boolean {
  return (
    (a.toLowerCase() === c.toLowerCase() &&
      b.toLowerCase() === d.toLowerCase()) ||
    (a.toLowerCase() === d.toLowerCase() && b.toLowerCase() === c.toLowerCase())
  );
}

export const SelectedPoolSection = () => {
  const { preparedList } = usePreparedPoolsRows('', true);
  const { currencyA, currencyB, mode, strategy, flow, provider } =
    useLiquidityWizardState();

  const filteredPools = useMemo(() => {
    if (!currencyA || !currencyB) {
      return null;
    }

    const vaults: PreparedListItem[] = [];
    const v4: PreparedListItem[] = [];
    const v2: PreparedListItem[] = [];

    preparedList
      .filter(item => {
        switch (item.view) {
          case 'v4': {
            return (
              mode === 'manual' &&
              flow === 'v4' &&
              arePairsEqual(
                currencyA.wrapped.address,
                currencyB.wrapped.address,
                item.data.pool.token0.id,
                item.data.pool.token1.id
              )
            );
          }
          case 'vaults': {
            return (
              mode === 'auto' &&
              provider?.toLowerCase() === item.data.provider.toLowerCase() &&
              strategy?.toLowerCase() === item.data.id?.toLowerCase()
            );
          }
          case 'v2': {
            return (
              mode === 'manual' &&
              flow === 'v2' &&
              arePairsEqual(
                currencyA.wrapped.address,
                currencyB.wrapped.address,
                item.data.token0.id,
                item.data.token1.id
              )
            );
          }
          default: {
            return false;
          }
        }
      })
      .forEach(item => {
        switch (item.view) {
          case 'vaults': {
            vaults.push(item);
            break;
          }
          case 'v4': {
            v4.push(item);
            break;
          }
          case 'v2': {
            v2.push(item);
            break;
          }
          default: {
            break;
          }
        }
      });

    return {
      vaults,
      v4,
      v2
    };
  }, [currencyA, currencyB, flow, mode, preparedList, provider, strategy]);

  return (
    <Box w="100%">
      {!!filteredPools?.vaults?.length && (
        <VStack width="100%" gap={4} alignItems="flex-start">
          {filteredPools.vaults.map(item => {
            const { marketMaker } = item.data;

            return (
              <VaultDetails
                key={item.data.id}
                item={item.data}
                marketMakerCampaign={marketMaker}
                isSelectable={false}
              />
            );
          })}
        </VStack>
      )}
      {!!filteredPools?.v4?.length && (
        <VStack width="100%" gap={4} alignItems="flex-start">
          {filteredPools.v4.map(item => {
            const { pool, badgesConfig, apr, marketMaker } = item.data;

            return (
              <PoolDetails
                badges={badgesConfig}
                key={pool.id}
                item={pool}
                apr={apr}
                marketMakerCampaign={marketMaker}
                isSelectable={false}
              />
            );
          })}
        </VStack>
      )}
      {!!filteredPools?.v2?.length && (
        <VStack width="100%" gap={4} alignItems="flex-start">
          {filteredPools.v2.map(item => {
            const { pairsDayData, ethPrice, id } = item.data;
            const dayData = pairsDayData?.filter(
              (p: { pairAddress: any }) => p.pairAddress === id
            );

            return (
              <PoolDetailsV2
                key={id}
                item={item.data}
                ethPrice={ethPrice}
                dayData={dayData}
              />
            );
          })}
        </VStack>
      )}
    </Box>
  );
};
